/* global globalThis */
// Persistent *model bytes*, never student handwriting. Shared across G12 and
// future levels on the same browser origin; version/hash keyed to avoid drift.
export const MODEL_CACHE_VERSION = 'dandu-ppocrv6-small-v1';
export const VENDOR_MODEL_BASE = 'https://paddle-model-ecology.bj.bcebos.com/paddlex/official_inference_model/paddle3.0.0';
export const OCR_MODEL_FILES = Object.freeze([
  {
    file: 'PP-OCRv6_small_det_onnx_infer.tar',
    name: 'PP-OCRv6_small_det',
    size: 9891840,
    sha256: 'd218f6fbf0f1c23d2161bd6ac7f5eaa6104fa89955c09290497e31008e2618e4',
  },
  {
    file: 'PP-OCRv6_small_rec_onnx_infer.tar',
    name: 'PP-OCRv6_small_rec',
    size: 21319680,
    sha256: 'd267ab077a44a0eedb1ea8f8c542d263f211de8e9d7a029bf9fcfff7e5a88fb1',
  },
]);

// A blocked storage implementation must not prevent verified online fallback.
function bounded(work, ms, message, onTimeout) {
  let timer;
  return Promise.race([Promise.resolve().then(work), new Promise((_, reject) => {
    timer = setTimeout(() => { onTimeout?.(); reject(new Error(message)); }, ms);
  })]).finally(() => clearTimeout(timer));
}

function hex(bytes) {
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
}

async function verify(buffer, spec, cryptoApi) {
  if (buffer.byteLength !== spec.size) throw new Error('OCR modeli boyutu doğrulanamadı.');
  if (!cryptoApi?.subtle?.digest) throw new Error('OCR model bütünlük kontrolü desteklenmiyor.');
  const digest = hex(await cryptoApi.subtle.digest('SHA-256', buffer));
  if (digest !== spec.sha256) throw new Error('OCR modeli SHA-256 doğrulaması başarısız.');
  return buffer;
}

function cacheKey(url, spec) {
  return url + (url.includes('?') ? '&' : '?') + 'dandu_model_sha256=' + spec.sha256;
}

// In-flight byte downloads share one promise per model/version in the active page.
const pendingBytes = new Map();

export async function loadVerifiedOcrModel(spec, {
  base,
  fetchImpl = globalThis.fetch,
  cacheApi = globalThis.caches,
  cryptoApi = globalThis.crypto,
  timeoutMs = 60000,
  storageTimeoutMs = 10000,
  onProgress,
  responseFactory = bytes => new Response(bytes, {
    status: 200, headers: { 'Content-Type': 'application/x-tar' },
  }),
} = {}) {
  const root = (base || VENDOR_MODEL_BASE).replace(/\/+$/, '');
  const source = root + '/' + spec.file;
  const key = cacheKey(source, spec);
  let entry = pendingBytes.get(key);
  if (!entry) {
    entry = { listeners: new Set(), latest: { loadedBytes: 0, totalBytes: spec.size } };
    pendingBytes.set(key, entry);
    const report = loadedBytes => {
      entry.latest = { loadedBytes, totalBytes: spec.size };
      for (const listener of entry.listeners) listener(entry.latest);
    };

    entry.promise = (async () => {
      const started = performance.now();
      let readMs = 0, verifyMs = 0, downloadMs = 0;
      const storage = work => bounded(work, storageTimeoutMs, 'OCR model deposuna erişilemedi.');
      let cache = null;
      try { if (cacheApi?.open) cache = await storage(() => cacheApi.open(MODEL_CACHE_VERSION)); } catch (_) { /* storage unsupported or full */ }
      if (cache) {
        try {
          const saved = await storage(() => cache.match(key));
          if (saved) {
            try {
              const readStarted = performance.now();
              const bytes = await storage(() => saved.arrayBuffer());
              readMs = performance.now() - readStarted;
              const verifyStarted = performance.now();
              const buffer = await verify(bytes, spec, cryptoApi);
              verifyMs = performance.now() - verifyStarted;
              report(spec.size);
              return { buffer, cacheHit: true, source, readMs, verifyMs, downloadMs, totalMs: performance.now() - started };
            } catch (_) {
              // Delete damaged/truncated bytes and re-fetch the exact pinned model.
              try { await storage(() => cache.delete(key)); } catch (_) { /* best effort */ }
            }
          }
        } catch (_) { /* cache unavailable; network fallback */ }
      }
      if (typeof fetchImpl !== 'function') throw new Error('OCR modeli için ağ erişimi bulunamadı.');
      const controller = new AbortController();
      const downloadStarted = performance.now();
      const bytes = await bounded(async () => {
        const result = await fetchImpl(source, { mode: 'cors', cache: 'default', signal: controller.signal });
        if (!result?.ok || result.type === 'opaque') throw new Error('OCR modeli indirilemedi.');
        // Pinned uncompressed TAR sizes remain accurate even without an exposed
        // Content-Length. Count actual bytes; never advance on a timer.
        if (!result.body?.getReader) {
          const buffer = await result.arrayBuffer();
          report(Math.min(buffer.byteLength, spec.size));
          return buffer;
        }
        const reader = result.body.getReader(), output = new Uint8Array(spec.size);
        let loaded = 0;
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (loaded + value.byteLength > spec.size) {
              controller.abort();
              throw new Error('OCR modeli boyutu doğrulanamadı.');
            }
            output.set(value, loaded); loaded += value.byteLength; report(loaded);
          }
        } finally { reader.releaseLock(); }
        if (loaded !== spec.size) throw new Error('OCR modeli boyutu doğrulanamadı.');
        return output.buffer;
      }, timeoutMs, 'OCR modeli zamanında indirilemedi. Yeniden deneyebilirsin.', () => controller.abort());
      downloadMs = performance.now() - downloadStarted;
      const verifyStarted = performance.now();
      const buffer = await verify(bytes, spec, cryptoApi);
      verifyMs = performance.now() - verifyStarted;
      if (cache) {
        try { await storage(() => cache.put(key, responseFactory(buffer))); } catch (_) { /* cache quota, private mode, browser policy */ }
      }
      return { buffer, cacheHit: false, source, readMs, verifyMs, downloadMs, totalMs: performance.now() - started };
    })().finally(() => { pendingBytes.delete(key); entry.listeners.clear(); });
  }
  if (onProgress) { entry.listeners.add(onProgress); onProgress(entry.latest); }
  try { return await entry.promise; } finally { entry.listeners.delete(onProgress); }
}

export async function loadOcrAssets({
  modelBase,
  specs = OCR_MODEL_FILES,
  urlApi = globalThis.URL,
  onProgress,
  ...dependencies
} = {}) {
  if (specs.length !== 2 || typeof urlApi?.createObjectURL !== 'function') {
    throw new Error('OCR modeli geçerli bir tarayıcıda açılamadı.');
  }
  const urls = [];
  const loaded = specs.map(() => 0), totalBytes = specs.reduce((sum, spec) => sum + spec.size, 0);
  let lastPercent = -1;
  const progress = (index, value) => {
    loaded[index] = value.loadedBytes;
    const loadedBytes = loaded.reduce((sum, bytes) => sum + bytes, 0);
    const percent = Math.floor(100 * loadedBytes / totalBytes);
    if (percent !== lastPercent) {
      lastPercent = percent;
      onProgress?.({ loadedBytes, totalBytes, percent });
    }
  };
  try {
    const resolved = await Promise.all(specs.map((spec, index) =>
      loadVerifiedOcrModel(spec, { base: modelBase, ...dependencies,
        onProgress: value => progress(index, value) })));
    const assets = resolved.map(({ buffer }) => {
      const url = urlApi.createObjectURL(new Blob([buffer], { type: 'application/x-tar' }));
      urls.push(url);
      return { url };
    });
    return {
      options: {
        textDetectionModelName: specs[0].name,
        textRecognitionModelName: specs[1].name,
        textDetectionModelAsset: assets[0],
        textRecognitionModelAsset: assets[1],
      },
      stats: resolved.map(({ buffer, ...timing }, i) => ({
        name: specs[i].name, bytes: specs[i].size, ...timing,
      })),
      release() {
        for (const url of urls.splice(0)) urlApi.revokeObjectURL(url);
      },
    };
  } catch (error) {
    for (const url of urls) urlApi.revokeObjectURL(url);
    throw error;
  }
}

// Background prefetch for first-grade mini-task entry points. Only the two
// immutable, SHA-verified model TAR files are loaded and stored. Do not
// initialize PaddleOCR/ORT here: that would block unrelated classroom games.
let grade1Prefetch = null;
export function prefetchGrade1OcrModels({
  modelBase = process.env.REACT_APP_DUY_VE_YAZ_MODEL_BASE,
  ...dependencies
} = {}) {
  if (grade1Prefetch) return grade1Prefetch;
  const running = Promise.all(OCR_MODEL_FILES.map(spec =>
    loadVerifiedOcrModel(spec, { base: modelBase, ...dependencies })))
    // Never pin the 31MB model ArrayBuffers in a long-lived resolved Promise:
    // Cache Storage has the bytes; OCR will load them on demand.
    .then(rows => rows.map(({ buffer, cacheHit, source, totalMs }) =>
      ({ cacheHit, source, totalMs, bytes: buffer.byteLength })));
  grade1Prefetch = running.catch(error => {
    grade1Prefetch = null; // allow a later visit to retry after offline/quota
    throw error;
  });
  return grade1Prefetch;
}
